import { API_URLS, fallbackReciters, fallbackTranslations, quranStructure } from './constants';

// Fetch all available editions (reciters and translations)
export const fetchEditions = async () => {
  try {
    const response = await fetch(API_URLS.EDITIONS);
    const data = await response.json();
    
    if (data.code === 200) {
      // Group editions by type
      const editionsByType = {};
      data.data.forEach(edition => {
        if (!editionsByType[edition.type]) {
          editionsByType[edition.type] = [];
        }
        editionsByType[edition.type].push(edition);
      });
      
      // Process translations - only include translations with language names
      const translations = (editionsByType.translation || [])
        .filter(translation => translation.language && translation.language !== '')
        .sort((a, b) => {
          // Sort English first, then alphabetically by language
          if (a.language === 'en' && b.language !== 'en') return -1;
          if (a.language !== 'en' && b.language === 'en') return 1;
          return a.language.localeCompare(b.language) || 
                 a.englishName.localeCompare(b.englishName);
        });
        
      // Process reciters - only include versebyverse audio editions
      const availableReciters = (editionsByType.versebyverse || [])
        .sort((a, b) => a.englishName.localeCompare(b.englishName));
      
      return {
        reciters: availableReciters.length > 0 ? availableReciters : fallbackReciters,
        translations: translations.length > 0 ? translations : fallbackTranslations
      };
    }
    throw new Error('Invalid response from API');
  } catch (error) {
    console.error("Error fetching editions:", error);
    // Fall back to hardcoded list
    return {
      reciters: fallbackReciters,
      translations: fallbackTranslations
    };
  }
};

export const fetchVerseData = async (surah, verse, reciter, translation) => {
  try {
    // Handle verse 0 case (Bismillah)
    const isVerseZero = verse === 0;
    
    // If this is verse 0, use a hardcoded Bismillah
    if (isVerseZero) {
      // Get proper surah information from quranStructure
      const surahInfo = quranStructure.find(s => s.number === surah) || { 
        number: surah, 
        name: `Surah ${surah}`, 
        englishName: `Surah ${surah}`, 
        totalVerses: 0 
      };
      
      // Return hardcoded Bismillah
      return {
        arabic: "بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ",
        translation: "In the name of Allah, the Entirely Merciful, the Especially Merciful.",
        transliteration: "Bismillāhi r-raḥmāni r-raḥīm",
        surahInfo: { 
          number: surahInfo.number, 
          name: surahInfo.name, 
          englishName: surahInfo.englishName, 
          numberOfAyahs: surahInfo.totalVerses 
        },
        translationEdition: { language: "en" },
        audioUrl: "" // No audio for Bismillah
      };
    }
    
    // For all other verses, fetch from API normally
    try {
      // Fetch Arabic verse
      const arabicResponse = await fetch(`${API_URLS.VERSE}/${surah}:${verse}/${reciter}`);
      // Fetch translation
      const translationResponse = await fetch(`${API_URLS.VERSE}/${surah}:${verse}/${translation}`);
      // Fetch transliteration directly from API
      const transliterationResponse = await fetch(`${API_URLS.VERSE}/${surah}:${verse}/en.transliteration`);
      
      if (!arabicResponse.ok || !translationResponse.ok || !transliterationResponse.ok) {
        throw new Error('Failed to fetch verse data from API');
      }
      
      const arabicData = await arabicResponse.json();
      const translationData = await translationResponse.json();
      const transliterationData = await transliterationResponse.json();
      
      if (arabicData.code !== 200 || translationData.code !== 200 || transliterationData.code !== 200) {
        throw new Error('API returned an error status');
      }
      
      // Return the regular verse data with transliteration
      return {
        arabic: arabicData.data.text,
        translation: translationData.data.text,
        transliteration: transliterationData.data.text,
        surahInfo: arabicData.data.surah,
        translationEdition: translationData.data.edition,
        audioUrl: arabicData.data.audio
      };
    } catch (error) {
      console.error("Error fetching regular verse:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in fetchVerseData:", error);
    throw error;
  }
};

export const searchQuran = async (keyword, surah = 'all', edition = 'en.asad') => {
  try {
    const response = await fetch(`https://api.alquran.cloud/v1/search/${encodeURIComponent(keyword)}/${surah}/${edition}`);
    
    if (!response.ok) {
      throw new Error('Search request failed');
    }
    
    const data = await response.json();
    
    if (data.code !== 200) {
      throw new Error(data.status || 'Search failed');
    }
    
    // If there are no matches, return empty array
    if (!data.data || !data.data.matches || data.data.matches.length === 0) {
      return [];
    }
    
    // We can directly use the data from the API
    const results = data.data.matches.map(match => ({
      surahNumber: match.surah.number,
      surahName: match.surah.name,
      surahEnglishName: match.surah.englishName,
      surahEnglishNameTranslation: match.surah.englishNameTranslation,
      revelationType: match.surah.revelationType,
      numberInSurah: match.numberInSurah,
      number: match.number,
      text: match.text,
      edition: match.edition
    }));
    
    // Return the count and matches
    return {
      count: data.data.count || results.length,
      matches: results
    };
  } catch (error) {
    console.error("Error searching Quran:", error);
    throw error;
  }
};